import * as THREE from 'three'
import { CATALOG, partDef } from './core/vessel/catalog'
import { GAIA } from './core/world/system'
import { altitudeOf } from './core/dynamics/flight'
import { densityAt } from './core/world/atmosphere'
import { fuelPathForEngine, availablePropellant } from './core/vessel/propellant'
import { vesselExtents } from './core/vessel/extents'
import { orbitReadouts } from './core/nav/readouts'
import { localUp, attitudeOf } from './core/nav/attitude'
import { length, vec3, Vec3 } from './core/math/vec3'
import { PartInstance, Vessel } from './core/vessel/vessel'
import { createSimulation, activeVessel, stepSimulation, stageUp, Simulation } from './game/simulation'
import { defaultRocket } from './game/vessels'
import { CAPO_GAIA } from './game/launch'
import { planSteps, FIXED_DT, MAX_STEPS_PER_FRAME } from './game/loop'
import { autopilotIniziale, desiredDirection, autopilotGimbal, AutopilotMode } from './game/autopilot'
import { createInput } from './game/input'
import { createRenderer, toScene } from './render/renderer'
import { createScene } from './render/scene'
import { createPlanet } from './render/planet'
import { createVesselViews } from './render/vesselView'
import { createCameraRig } from './render/cameras'
import { createHud } from './ui/hud'
import { createNavball } from './ui/navball'
import { createMappa } from './render/mappa'
import { createPannelloMappa } from './ui/mappa'
import { elementsFromState, apoapsisRadius } from './core/orbit/elements'
import { prossimiEventi, tempoAlProssimo, Evento } from './core/orbit/prediction'
import {
  Manovra, manovraNulla, applicaManovra, deltaVDi, direzioneManovra,
  manovraPerCircolarizzare,
} from './core/orbit/maneuver'
import { impostaTimeWarp, FATTORI_TEMPO, MASSIMO_FISICO } from './game/simulation'
import { stateFromElements } from './core/orbit/elements'
import { propagate } from './core/orbit/kepler'
import { Design, daVeicolo, designVuoto } from './editor/design'
import {
  aggiungiPrima, aggiungiAssiale, aggiungiRadiale, togliParte,
  spostaParte, ruotaParte, assegnaStadio, rinumeraStadi,
} from './editor/assembly'
import { agganciPossibili } from './editor/attach'
import { calcolaIndicatori } from './editor/indicators'
import { createEditorScene } from './editor/scene'
import { createEditorControls } from './editor/interazione'
import { createOfficina } from './ui/officina'
import { salvaLocale, caricaLocale, elencoLocale } from './editor/storage'

export type Schermata = 'officina' | 'volo'

export interface ApogeoReadouts {
  quota: number; velocita: number; apoapside: number; periapside: number
  tempoApoapside: number; propellenteStadio: number; beccheggio: number
  rotta: number; massa: number; tempo: number
}

export interface ApogeoDebug {
  readonly schermata: () => Schermata
  readonly sim: () => Simulation | null
  readonly design: () => Design
  avanza(secondi: number): void
  readouts(): ApogeoReadouts
  stage(): boolean
  setThrottle(v: number): void
  puntaVerso(beccheggioGradi: number, rottaGradi: number): void
  puntaProgrado(): void
  autopilota(mode: AutopilotMode): void
  // officina
  officinaPrendi(defId: string | null): void
  officinaSeleziona(partId: string | null): void
  /** posa la parte in mano su un nodo preciso: il puntamento col mouse e provato a parte */
  officinaPosa(partId: string, nodeId: string): boolean
  officinaPosaRadiale(partId: string, quota: number, azimut: number): boolean
  officinaSelezione(): string | null
  officinaSposta(dx: number, dy: number, dz: number): void
  officinaElimina(): void
  officinaIndicatori(): ReturnType<typeof calcolaIndicatori>
  perso(): boolean
  caricoStrutturale(): number
  temperaturaMassima(): number
  apriParacadute(v: boolean): void
  statoParacadute(): string
  // mappa e manovre
  mappaAperta(): boolean
  apriMappa(v: boolean): void
  corpoRiferimento(): string
  eventiPrevisti(): Evento[]
  creaNodo(): void
  circolarizza(): void
  regolaNodo(delta: number): void
  nodo(): Manovra | null
  puntaAlNodo(): void
  warp(fattore: number): boolean
  fattoreTempo(): number
  suRotaia(): boolean
  lancia(): boolean
  tornaInOfficina(): void
  debugMeshCount(): number
  debugPlumeCount(): number
  debugVesselGroups(): number
}

/** I serbatoi tornano pieni quando il razzo va sulla rampa. */
function rifornito(vessel: Vessel): Vessel {
  return {
    rootId: vessel.rootId,
    parts: vessel.parts.map((p): PartInstance => ({
      ...p, propellant: partDef(CATALOG, p.defId).tank?.propellantMass ?? 0,
    })),
  }
}

export function avviaApp(): ApogeoDebug {
  const canvas = document.getElementById('scena') as HTMLCanvasElement
  const { camera, render, resize } = createRenderer(canvas)

  // ── stato
  let schermata: Schermata = 'officina'
  let design: Design = daVeicolo(defaultRocket(), 'Vettore Uno')
  let selezione: string | null = null
  let inMano: string | null = null
  let simmetria = 4
  let messaggio: string | null = null
  let messaggioFino = 0
  let sim: Simulation | null = null
  let vistaMappa = false
  let manovra: Manovra | null = null
  let mappaYaw = 0.6
  let mappaPitch = 0.9
  let mappaZoom = 1

  const ap = autopilotIniziale()

  // ── volo
  const { scene: scenaVolo, stars } = createScene()
  const pianeta = createPlanet(GAIA)
  scenaVolo.add(pianeta.group)
  const viste = createVesselViews(scenaVolo, CATALOG)
  const rigVolo = createCameraRig()
  rigVolo.attach(canvas)

  const hudRoot = document.getElementById('hud') as HTMLElement
  const navballCanvas = document.getElementById('navball') as HTMLCanvasElement
  const aiuto = document.getElementById('aiuto') as HTMLElement
  const avviso = document.getElementById('avviso') as HTMLElement
  const hud = createHud(hudRoot)
  const navball = createNavball(navballCanvas)
  const mappa = createMappa()
  const mappaRoot = document.getElementById('mappa-ui') as HTMLElement
  const pannelloMappa = createPannelloMappa(mappaRoot)

  // ── officina
  const scenaOfficina = createEditorScene(CATALOG)
  const controlliOfficina = createEditorControls()
  controlliOfficina.attach(canvas)
  const officinaRoot = document.getElementById('officina') as HTMLElement

  function segnala(testo: string): void {
    messaggio = testo
    messaggioFino = performance.now() + 3200
    avviso.textContent = testo
    avviso.hidden = false
  }

  const officina = createOfficina(officinaRoot, CATALOG, {
    prendi(defId) { inMano = defId === inMano ? null : defId; selezione = null },
    simmetria(n) { simmetria = n },
    sposta(off) { if (selezione) design = spostaParte(design, selezione, off) },
    ruota(asse, angolo) { if (selezione) design = ruotaParte(design, selezione, asse, angolo) },
    stadio(delta) {
      if (!selezione) return
      const p = design.vessel.parts.find((x) => x.id === selezione)!
      design = rinumeraStadi(assegnaStadio(design, selezione, p.stage + delta))
    },
    elimina() {
      if (!selezione) return
      design = togliParte(design, selezione)
      selezione = null
    },
    lancia() { lancia() },
    salva() {
      salvaLocale(design, new Date().toISOString())
      segnala(`Progetto «${design.nome}» salvato`)
    },
    carica(nome) {
      try {
        const d = caricaLocale(nome, CATALOG)
        if (d) { design = d; selezione = null; inMano = null; segnala(`Caricato «${nome}»`) }
      } catch (e) {
        segnala(e instanceof Error ? e.message : 'Progetto illeggibile')
      }
    },
    nuovo() { design = designVuoto('Senza nome'); selezione = null; inMano = null },
  })

  // ── posa delle parti
  function posa(scelta: ReturnType<typeof controlliOfficina.punta>): void {
    if (!scelta) { if (!inMano) selezione = null; return }

    if (!inMano) {
      selezione = scelta.tipo === 'nodo' ? scelta.nodo.partId
        : scelta.tipo === 'parte' ? scelta.id : scelta.partId
      return
    }

    if (design.vessel.parts.length === 0) {
      design = aggiungiPrima(design, CATALOG, inMano)
      return
    }

    if (scelta.tipo === 'nodo') {
      const a = agganciPossibili(design.vessel, CATALOG, inMano)
        .find((x) => x.parentId === scelta.nodo.partId && x.parentNodeId === scelta.nodo.nodeId)
      if (a) design = aggiungiAssiale(design, CATALOG, inMano, a)
      else segnala('Quella parte non si aggancia lì')
      return
    }

    if (scelta.tipo === 'fianco') {
      const def = partDef(CATALOG, inMano)
      if (!def.nodes.some((n) => n.kind === 'radial')) {
        segnala('Questa parte si monta solo in linea, non di fianco')
        return
      }
      design = aggiungiRadiale(design, CATALOG, inMano, scelta.partId, scelta.quota, scelta.azimut, simmetria)
    }
  }

  // la camera della mappa gira col trascinamento e zooma con la rotella
  let trascinaMappa = false
  let mx = 0
  let my = 0
  canvas.addEventListener('pointerdown', (e) => {
    if (vistaMappa) { trascinaMappa = true; mx = e.clientX; my = e.clientY }
  })
  window.addEventListener('pointerup', () => { trascinaMappa = false })
  window.addEventListener('pointermove', (e) => {
    if (!trascinaMappa) return
    mappaYaw -= (e.clientX - mx) * 0.006
    mappaPitch = Math.max(-1.5, Math.min(1.5, mappaPitch + (e.clientY - my) * 0.006))
    mx = e.clientX; my = e.clientY
  })
  canvas.addEventListener('wheel', (e) => {
    if (!vistaMappa) return
    e.preventDefault()
    mappaZoom = Math.max(0.15, Math.min(12, mappaZoom * Math.exp(e.deltaY * 0.0012)))
  }, { passive: false })

  canvas.addEventListener('pointerup', (e) => {
    if (schermata !== 'officina' || controlliOfficina.haTrascinato()) return
    posa(controlliOfficina.punta(e, canvas, camera, scenaOfficina, design, CATALOG))
  })

  // ── passaggio fra le schermate
  function lancia(): boolean {
    const ind = calcolaIndicatori(design, CATALOG, GAIA)
    if (ind.avvisi.some((a) => a.gravita === 'errore')) {
      segnala('Il razzo non è pronto: risolvi gli errori elencati')
      return false
    }
    design = rinumeraStadi(design)
    sim = createSimulation(CATALOG, GAIA, CAPO_GAIA, rifornito(design.vessel))
    ap.mode = 'spento'
    schermata = 'volo'
    officinaRoot.hidden = true
    hudRoot.hidden = false
    navballCanvas.hidden = false
    aiuto.hidden = false
    return true
  }

  function tornaInOfficina(): void {
    sim = null
    schermata = 'officina'
    officinaRoot.hidden = false
    hudRoot.hidden = true
    navballCanvas.hidden = true
    aiuto.hidden = true
  }

  const input = createInput({
    stadio() { if (sim && !stageUp(sim)) segnala('Non ci sono altri stadi') },
    paracadute() {
      if (!sim) return
      const sv = activeVessel(sim)
      if (sv.paracadute.length === 0) { segnala('Nessun paracadute a bordo'); return }
      sv.paracaduteAperti = !sv.paracaduteAperti
      segnala(sv.paracaduteAperti ? 'Paracadute in apertura' : 'Paracadute richiusi')
    },
    cambiaCamera() { rigVolo.toggle() },
    cambiaAutopilota() {
      if (!sim) return
      ap.mode = ap.mode === 'spento' ? 'assetto' : 'spento'
      if (ap.mode === 'assetto') {
        const sv = activeVessel(sim)
        const a = attitudeOf(sv.state.position, sv.state.orientation)
        ap.pitchTarget = a.pitch
        ap.headingTarget = a.heading
      }
      segnala(ap.mode === 'spento' ? 'Pilota automatico spento' : 'Pilota automatico: mantieni assetto')
    },
    progrado() {
      if (!sim) return
      ap.mode = ap.mode === 'prograde' ? 'spento' : 'prograde'
      segnala(ap.mode === 'prograde' ? 'Pilota automatico: prograda' : 'Pilota automatico spento')
    },
    puntaAlNodo() {
      const el = orbitaCorrente()
      if (!el || !manovra) { segnala('Nessun nodo di manovra'); return }
      ap.mode = 'bersaglio'
      ap.bersaglio = direzioneManovra(el, manovra)
      segnala('Pilota automatico: punta al nodo')
    },
    warp(d) {
      if (!sim) return
      const i = FATTORI_TEMPO.indexOf(sim.timeWarp as typeof FATTORI_TEMPO[number])
      const voluto = FATTORI_TEMPO[Math.max(0, Math.min(FATTORI_TEMPO.length - 1, i + d))]!
      if (!impostaTimeWarp(sim, voluto)) {
        segnala(voluto > MASSIMO_FISICO
          ? 'Per accelerare oltre ×4 servono i motori spenti, fuori dall’atmosfera'
          : 'Fattore di tempo non valido')
        return
      }
      segnala(`Tempo ×${sim.timeWarp}`)
    },
    mappa() {
      vistaMappa = !vistaMappa
      mappaRoot.hidden = !vistaMappa
      navballCanvas.hidden = vistaMappa
    },
  })

  /** L'orbita corrente del veicolo attivo, nel riferimento in uso. */
  function orbitaCorrente(): ReturnType<typeof elementsFromState> | null {
    if (!sim || sim.perso || sim.vessels.length === 0) return null
    const sv = activeVessel(sim)
    return elementsFromState(sv.state.position, sv.state.velocity, sim.body.mu, sim.time)
  }

  function creaNodoApoapside(): void {
    const el = orbitaCorrente()
    if (!el) return
    manovra = manovraNulla(tempoAlProssimo(el, Math.PI))
    segnala('Nodo di manovra all’apoapside: regola il Δv con [ e ]')
  }

  function regolaNodo(delta: number): void {
    if (!manovra) return
    manovra = { ...manovra, prograde: manovra.prograde + delta }
  }

  function circolarizza(): void {
    const el = orbitaCorrente()
    if (!el) return
    manovra = manovraPerCircolarizzare(el, tempoAlProssimo(el, Math.PI))
    segnala(`Manovra di circolarizzazione: ${Math.round(deltaVDi(manovra))} m/s`)
  }

  window.addEventListener('keydown', (e) => {
    if (e.code === 'Delete' || e.code === 'Backspace') {
      if (schermata === 'officina' && selezione) {
        e.preventDefault()
        design = togliParte(design, selezione)
        selezione = null
      }
    }
    if (e.code === 'KeyB' && schermata === 'volo') tornaInOfficina()
    if (schermata === 'volo' && sim) {
      if (e.code === 'KeyN') creaNodoApoapside()
      if (e.code === 'KeyK') circolarizza()
      if (e.code === 'BracketLeft') regolaNodo(-10)
      if (e.code === 'BracketRight') regolaNodo(10)
      if (e.code === 'Delete' && manovra) { manovra = null; segnala('Nodo tolto') }
    }
    if (e.code === 'Escape' && schermata === 'officina') { inMano = null; selezione = null }
  })

  aiuto.innerHTML = [
    '<b>Spazio</b> stadio successivo · <b>Z</b> / <b>X</b> manetta',
    '<b>W A S D</b> assetto · <b>T</b> pilota · <b>F</b> prograda',
    '<b>M</b> mappa · <b>N</b> nodo · <b>K</b> circolarizza · <b>G</b> punta al nodo',
    '<b>P</b> paracadute',
    '<b>C</b> camera · <b>,</b> <b>.</b> tempo · <b>B</b> officina',
  ].join('<br>')

  // ── passo di volo
  function passoVolo(dt: number): void {
    if (!sim || sim.perso) return
    const sv = activeVessel(sim)
    if (ap.mode !== 'spento' && !input.chiedeAssetto()) {
      sv.controls = { throttle: input.comandi.throttle, gimbal: autopilotGimbal(sv, desiredDirection(sim, sv, ap)) }
    } else {
      if (ap.mode !== 'spento') ap.mode = 'spento'
      sv.controls = {
        throttle: input.comandi.throttle,
        gimbal: { pitch: input.comandi.pitch, yaw: input.comandi.yaw },
      }
    }
    stepSimulation(sim, dt)
  }

  // ── disegno
  let ultimoHud = 0
  function disegna(ora: number): void {
    // veicolo disintegrato: non c'e piu niente da inseguire con la camera
    if (schermata === 'volo' && sim && sim.perso) {
      if (avviso.hidden) segnala('Veicolo distrutto — premi B per tornare in officina')
      render(scenaVolo)
      return
    }

    if (schermata === 'volo' && sim && vistaMappa) {
      const sv = activeVessel(sim)
      const el = orbitaCorrente()!
      const eventi = prossimiEventi(el, sim.body, sim.time, Math.max(60_000, (orbitReadouts(sv.state, sim.body).period || 1e5) * 1.5))
      const orbitaManovra = manovra && deltaVDi(manovra) > 0 ? applicaManovra(el, manovra) : null
      const posizioneManovra = manovra ? stateFromElements(propagate(el, manovra.quando)).position : null

      const dati = {
        body: sim.body, time: sim.time, orbita: el,
        posizione: sv.state.position, eventi, orbitaManovra, posizioneManovra,
      }
      mappa.aggiorna(dati)
      mappa.inquadra(camera, dati, mappaYaw, mappaPitch, mappaZoom)
      render(mappa.scene)

      if (ora - ultimoHud > 150) {
        pannelloMappa.aggiorna({
          body: sim.body,
          inclinazione: el.inc,
          letture: orbitReadouts(sv.state, sim.body),
          eventi,
          manovra,
          orbitaManovra,
          timeWarp: sim.timeWarp,
          suRotaia: sv.rails !== null,
        })
        hud.update(sim, sv, ap)
        ultimoHud = ora
      }
      return
    }

    if (schermata === 'volo' && sim) {
      const sv = activeVessel(sim)
      const origine = sv.state.position
      const su = localUp(origine)
      pianeta.update(sim.time, origine)
      viste.sync(sim, origine, (v) => v.controls.throttle)
      const ext = vesselExtents(sv.vessel, CATALOG)
      rigVolo.update(camera, toScene(sv.state.position, origine),
        new THREE.Vector3(su.x, su.y, su.z), Math.max(ext.height / 2, ext.maxRadius))
      stars.position.copy(camera.position)
      const rho = densityAt(GAIA.atmosphere, altitudeOf(origine, GAIA))
      ;(stars.material as THREE.PointsMaterial).opacity = Math.max(0, Math.min(1, 1 - rho * 6))
      render(scenaVolo)
      navball.update(sim, sv)
      if (ora - ultimoHud > 100) { hud.update(sim, sv, ap); ultimoHud = ora }
      return
    }

    const ind = calcolaIndicatori(design, CATALOG, GAIA)
    const ext = design.vessel.parts.length
      ? vesselExtents(design.vessel, CATALOG)
      : { minY: 0, maxY: 4, maxRadius: 2, height: 4 }
    scenaOfficina.ricostruisci(design, ind, selezione, inMano !== null, ext.height)
    controlliOfficina.update(camera, ext.height, (ext.minY + ext.maxY) / 2)
    render(scenaOfficina.scene)
    if (ora - ultimoHud > 120) {
      officina.aggiorna({
        design, indicatori: ind, selezione, inMano, simmetria,
        salvati: elencoLocale(), messaggio: null,
      })
      ultimoHud = ora
    }
  }

  let accumulatore = 0
  let ultimo = performance.now()

  function frame(ora: number): void {
    const piano = planSteps(accumulatore, (ora - ultimo) / 1000, FIXED_DT, MAX_STEPS_PER_FRAME)
    ultimo = ora
    accumulatore = piano.accumulator

    if (schermata === 'volo' && sim) {
      input.aggiorna(Math.min(0.1, piano.steps * FIXED_DT) || FIXED_DT)
      if (sim.timeWarp > MASSIMO_FISICO) {
        // su rotaia si fa un solo passo grande: mille passi piccoli non
        // servirebbero a niente e costerebbero mille volte tanto
        if (piano.steps > 0) passoVolo(FIXED_DT * piano.steps * sim.timeWarp)
      } else {
        for (let i = 0; i < piano.steps * sim.timeWarp; i++) passoVolo(FIXED_DT)
      }
    }

    disegna(ora)
    if (!avviso.hidden && ora > messaggioFino) { avviso.hidden = true; messaggio = null }
    requestAnimationFrame(frame)
  }

  tornaInOfficina()
  resize()
  requestAnimationFrame(frame)

  function letture(): ApogeoReadouts {
    if (!sim || sim.perso || sim.vessels.length === 0) {
      return { quota: 0, velocita: 0, apoapside: 0, periapside: 0, tempoApoapside: 0,
        propellenteStadio: 0, beccheggio: 0, rotta: 0, massa: 0, tempo: 0 }
    }
    const sv = activeVessel(sim)
    const ro = orbitReadouts(sv.state, sim.body)
    const serbatoi = new Set<string>()
    for (const e of sv.activeEngines) {
      for (const t of fuelPathForEngine(sv.vessel, CATALOG, e)) serbatoi.add(t)
    }
    const att = attitudeOf(sv.state.position, sv.state.orientation)
    return {
      quota: altitudeOf(sv.state.position, sim.body),
      velocita: length(sv.state.velocity),
      apoapside: ro.apoapsis,
      periapside: ro.periapsis,
      tempoApoapside: ro.timeToApoapsis,
      propellenteStadio: availablePropellant(sv.vessel, [...serbatoi]),
      beccheggio: (att.pitch * 180) / Math.PI,
      rotta: (att.heading * 180) / Math.PI,
      massa: sv.massProps.mass,
      tempo: sim.time,
    }
  }

  return {
    schermata: () => schermata,
    sim: () => sim,
    design: () => design,
    avanza(secondi) {
      const passi = Math.max(0, Math.round(secondi / FIXED_DT))
      for (let i = 0; i < passi; i++) passoVolo(FIXED_DT)
    },
    readouts: letture,
    stage: () => (sim ? stageUp(sim) : false),
    setThrottle(v) { input.comandi.throttle = Math.max(0, Math.min(1, v)) },
    puntaVerso(b, r) {
      ap.mode = 'assetto'
      ap.pitchTarget = (b * Math.PI) / 180
      ap.headingTarget = (r * Math.PI) / 180
    },
    puntaProgrado() { ap.mode = 'prograde' },
    autopilota(mode) { ap.mode = mode },
    officinaPrendi(defId) { inMano = defId; selezione = null },
    officinaSeleziona(partId) { selezione = partId; inMano = null },
    officinaSelezione: () => selezione,
    officinaPosa(partId, nodeId) {
      if (!inMano) return false
      if (design.vessel.parts.length === 0) { design = aggiungiPrima(design, CATALOG, inMano); return true }
      const a = agganciPossibili(design.vessel, CATALOG, inMano)
        .find((x) => x.parentId === partId && x.parentNodeId === nodeId)
      if (!a) return false
      design = aggiungiAssiale(design, CATALOG, inMano, a)
      return true
    },
    officinaPosaRadiale(partId, quota, azimut) {
      if (!inMano) return false
      const prima = design.vessel.parts.length
      design = aggiungiRadiale(design, CATALOG, inMano, partId, quota, azimut, simmetria)
      return design.vessel.parts.length > prima
    },
    officinaSposta(dx, dy, dz) { if (selezione) design = spostaParte(design, selezione, vec3(dx, dy, dz)) },
    officinaElimina() { if (selezione) { design = togliParte(design, selezione); selezione = null } },
    officinaIndicatori: () => calcolaIndicatori(design, CATALOG, GAIA),
    perso: () => (sim ? sim.perso : false),
    caricoStrutturale: () => (sim && !sim.perso ? activeVessel(sim).caricoStrutturale : 0),
    temperaturaMassima() {
      if (!sim || sim.perso || sim.vessels.length === 0) return 0
      const t = Object.values(activeVessel(sim).termico.temperature)
      return t.length ? Math.max(...t) : 0
    },
    apriParacadute(v) { if (sim && !sim.perso) activeVessel(sim).paracaduteAperti = v },
    statoParacadute() {
      if (!sim || sim.perso || sim.vessels.length === 0) return 'nessuno'
      return activeVessel(sim).paracadute[0]?.stato ?? 'nessuno'
    },
    mappaAperta: () => vistaMappa,
    apriMappa(v) { vistaMappa = v; mappaRoot.hidden = !v; navballCanvas.hidden = v },
    corpoRiferimento: () => (sim ? sim.body.id : ''),
    eventiPrevisti() {
      const el = orbitaCorrente()
      if (!el || !sim) return []
      return prossimiEventi(el, sim.body, sim.time, 400_000)
    },
    creaNodo: creaNodoApoapside,
    circolarizza,
    regolaNodo,
    nodo: () => manovra,
    puntaAlNodo() {
      const el = orbitaCorrente()
      if (!el || !manovra) return
      ap.mode = 'bersaglio'
      ap.bersaglio = direzioneManovra(el, manovra)
    },
    warp(fattore) { return sim ? impostaTimeWarp(sim, fattore) : false },
    fattoreTempo: () => (sim ? sim.timeWarp : 1),
    suRotaia: () => (sim ? activeVessel(sim).rails !== null : false),
    lancia,
    tornaInOfficina,
    debugMeshCount: () => viste.meshCount(),
    debugPlumeCount: () => viste.plumeCount(),
    debugVesselGroups: () => viste.groupCount(),
  }
}
